import { ProductResource } from '@df/catalog/product/product.resource';
import { NProductConfiguration } from '@df/module-product-configuration/df-module-product-configuration.interfaces';
import { Nexus } from '@tomandco/nexus-api-types';
import { ProductConfigurationBundle } from '../../product-configuration-bundle';
import { BundleOptionValue } from './bundle-option-value';

export class BundleOption {
    /**
     * Value instance class
     */
    static valueInstance = BundleOptionValue;

    /**
     * Bundle selection as map of selection id => quantity
     */
    private selection = new Map<number, number>();

    /**
     * IMPORTANT: Single selection only
     */
    selectedValue: BundleOptionValue | undefined;

    /**
     * Option values
     */
    readonly values: BundleOptionValue[];

    /**
     * If multiple options are allowed
     */
    private isMultiSelect = ['checkbox', 'multi'].includes(this.data.type);

    /**
     * Dependencies
     */
    constructor(
        readonly data: Nexus.Lib.Catalog.Type.IProductDataBundleOption,
        protected productResource: ProductResource,
        private configuration?: ProductConfigurationBundle
    ) {
        this.values = this.toValues(data.children);
        const defaultSelection = data.defaultSelection;
        if (defaultSelection) {
            const selectedValues = Array.isArray(defaultSelection) ? defaultSelection : [defaultSelection];
            this.values
                .filter(value => selectedValues.includes(value.id))
                .forEach(value => {
                    value.selected = true;
                });
        }
    }

    protected toValues(values: Nexus.Lib.Catalog.Type.IProductDataBundleOptionChild[]): BundleOptionValue[] {
        return values.map(value => new BundleOption.valueInstance(this, this.productResource, value));
    }

    /**
     * If value is selected test
     */
    isSelected(selectionValue: number): boolean {
        const selection = this.selection.get(selectionValue);
        return selection !== undefined && selection > 0;
    }

    /**
     * Qty selection
     */
    getQty(selectionValue: number): number {
        return this.selection.get(selectionValue) || 0;
    }

    setQty(selectionValue: number, qty: number) {
        if (qty > 0) {
            if (!this.isMultiSelect) {
                this.selection.forEach((qty, selectionId) => {
                    if (qty && selectionId !== selectionValue) {
                        this.setValueSelection(selectionId, 0);
                    }
                });
            }
            this.setValueSelection(selectionValue, qty);
        } else {
            this.setValueSelection(selectionValue, 0);
        }
        this.onChange();
    }

    private setValueSelection(value: number, qty: number) {
        qty ? this.selection.set(value, qty) : this.selection.delete(value);
        this.values.find(i => i.id === value)?.change$.next();
    }

    /**
     * Selection value
     * IMPORTANT: this is only to be used with single select options
     */
    get value(): number | undefined {
        return this.selectedValue?.id;
    }

    set value(selectionId: number | undefined) {
        if (selectionId) {
            if (typeof selectionId === 'string') {
                selectionId = Number(selectionId);
            }
            this.values.find(value => value.id === selectionId)!.selected = true;
        } else {
            this.selection.forEach((qty, selectionId) => {
                if (qty) {
                    this.setValueSelection(selectionId, 0);
                }
            });
        }
        this.onChange();
    }

    /**
     * Total option price getter
     */
    get price(): number {
        return this.values.reduce((price, value) => price + value.rowPrice, 0);
    }

    /**
     * Returns selection in buy request format
     */
    toRequest(): { key: number; value: Nexus.Lib.Basket.BundleOptionSelectionSelection } {
        const request: Nexus.Lib.Basket.BundleOptionSelectionSelection = {};
        this.selection.forEach((qty, selectionId) => {
            request[selectionId] = qty;
        });
        return { key: this.data.id, value: request };
    }

    /**
     * Validate selection
     */
    validate(): NProductConfiguration.Error.Entry | undefined {
        if (this.data.required) {
            if (!this.selection.size) {
                return {
                    code: NProductConfiguration.Error.Code.missing,
                    level: NProductConfiguration.Error.Level.error,
                    fieldName: this.data.title
                };
            }
        }
    }

    /**
     * Update selected value for single-choice options only
     */
    private onChange() {
        this.configuration?.bundleOptionsDataChange$.next(this);
        if (!this.isMultiSelect) {
            this.selectedValue = this.values.find(i => i.selected);
        }
    }
}
